// Database lifecycle lives outside React so StrictMode double-mounts can't open the DB twice.
import { DbClient } from './client';
import { DbError, type DbMode } from './protocol';
import { listenForReleaseRequests, locksSupported, takeOverDbLock, tryAcquireDbLock, type DbLock } from './tabLock';
import { requestPersistentStorage } from '../lib/storage';
import { seedDemo } from '../demo/seed';

/** Which database to open: the in-memory demo, or a signed-in account's file. */
export type DbTarget = { mode: 'demo' } | { mode: 'real'; userId: string };

export type DbState =
  | { status: 'idle' }
  | { status: 'starting' }
  | { status: 'ready'; client: DbClient; mode: DbMode }
  | { status: 'other-tab' }
  | { status: 'unsupported'; message: string }
  | { status: 'error'; message: string };

interface OpenDb {
  key: string;
  client: DbClient;
  lock: DbLock | null;
  stopListening: () => void;
}

let state: DbState = { status: 'idle' };
let current: OpenDb | null = null;
let currentKey: string | null = null;
let lastTarget: DbTarget | null = null;
const listeners = new Set<() => void>();

function setState(next: DbState) {
  state = next;
  listeners.forEach((listener) => listener());
}

export function getDbState() {
  return state;
}

export function subscribeDbState(listener: () => void) {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

export function requestedMode(): DbMode {
  return new URLSearchParams(window.location.search).has('demo') ? 'demo' : 'real';
}

function keyOf(target: DbTarget) {
  return target.mode === 'demo' ? 'demo' : `user:${target.userId}`;
}

const sleep = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

/**
 * A worker terminated moments ago (sign-out, account switch, tab handover) may still hold its OPFS
 * access handles, so opening the pool can fail transiently. Retry a few times before giving up.
 */
async function openClient(target: DbTarget): Promise<DbClient> {
  for (let attempt = 0; ; attempt++) {
    const client = new DbClient();
    try {
      await client.open(target.mode, target.mode === 'real' ? target.userId : undefined);
      return client;
    } catch (error) {
      client.terminate();
      const permanent = error instanceof DbError && (error.code === 'UNSUPPORTED' || error.code === 'INVALID');
      if (permanent || target.mode === 'demo' || attempt >= 4) throw error;
      await sleep(150 * (attempt + 1));
    }
  }
}

/** Lock release is asynchronous, so a lock this tab just released may briefly look taken. */
async function acquireLockSoon(): Promise<DbLock | null> {
  for (let attempt = 0; attempt < 3; attempt++) {
    const lock = await tryAcquireDbLock();
    if (lock) return lock;
    await sleep(100);
  }
  return null;
}

async function openWith(target: DbTarget, key: string, lock: DbLock | null) {
  let client: DbClient;
  try {
    client = await openClient(target);
  } catch (error) {
    lock?.release();
    if (currentKey !== key) return;
    if (error instanceof DbError && error.code === 'UNSUPPORTED') setState({ status: 'unsupported', message: error.message });
    else setState({ status: 'error', message: error instanceof Error ? error.message : String(error) });
    return;
  }
  if (currentKey !== key) {
    // A different target was requested while this one was opening.
    client.terminate();
    lock?.release();
    return;
  }
  const stopListening = lock
    ? listenForReleaseRequests(() => {
        stopListening();
        client.terminate();
        lock.release();
        current = null;
        setState({ status: 'other-tab' });
      })
    : () => undefined;
  current = { key, client, lock, stopListening };
  if (target.mode === 'real') void requestPersistentStorage();
  else await seedDemo(client);
  setState({ status: 'ready', client, mode: target.mode });
}

/** Open the database for `target`. Calling again with the same target is a no-op. */
export async function openDb(target: DbTarget) {
  const key = keyOf(target);
  if (currentKey === key) return;
  closeCurrent();
  currentKey = key;
  lastTarget = target;
  setState({ status: 'starting' });
  if (target.mode === 'demo') {
    // The demo DB is in memory and never touches OPFS, so it needs no lock.
    await openWith(target, key, null);
    return;
  }
  if (!locksSupported()) {
    setState({ status: 'unsupported', message: 'This browser is missing features RenalFlow needs (Web Locks). Please update it.' });
    return;
  }
  const lock = await acquireLockSoon();
  if (currentKey !== key) {
    lock?.release();
    return;
  }
  if (!lock) {
    setState({ status: 'other-tab' });
    return;
  }
  await openWith(target, key, lock);
}

function closeCurrent() {
  if (!current) return;
  current.stopListening();
  current.client.terminate();
  current.lock?.release();
  current = null;
}

/**
 * Close the open database. With `destroy`, its files are deleted from this device first (sign-out).
 */
export async function closeDb({ destroy = false }: { destroy?: boolean } = {}) {
  if (destroy && current) await current.client.destroy();
  closeCurrent();
  currentKey = null;
  setState({ status: 'idle' });
}

/** "Use here": ask the other tab to release the database, then open it in this tab. */
export async function takeOverDb() {
  if (!lastTarget || lastTarget.mode !== 'real') return;
  const target = lastTarget;
  const key = keyOf(target);
  currentKey = key;
  setState({ status: 'starting' });
  const lock = await takeOverDbLock();
  if (currentKey !== key) {
    lock.release();
    return;
  }
  await openWith(target, key, lock);
}
